import type { Row } from "@rocicorp/zero";
import {
  type CrossResourceAvailabilityRuleId,
  type GrantRow,
  isSupplierAvailableFromGrants,
  principalHasSupplierPermissionFromGrants,
} from "../../shared/rbac/crossResourceAvailability";
import type { GrantEntry } from "../../shared/rbac/roles";
import type { PrincipalPreview } from "../components/PrincipalPreviewList";
import { userDisplayName } from "./userDisplayName";

type CustomRoleLike = {
  id: string;
  rolePermissions?: ReadonlyArray<{ permission: string }>;
};

function stagedToGrantRow(
  g: GrantEntry,
  roleById: ReadonlyMap<string, CustomRoleLike>
): GrantRow {
  return {
    userGroupId: g.userGroupId ?? null,
    userId: g.userId ?? null,
    serviceAccountId: g.serviceAccountId ?? null,
    builtinRoleId: g.builtinRoleId ?? null,
    customRole: g.customRoleId ? (roleById.get(g.customRoleId) ?? null) : null,
  };
}

// Users who lose `connected_app:use` when a connector's grants change — removed
// directly or as a member of a removed group, and reaching it no other way
// afterwards. Mirrors the server's personal-chat drop (brokenChatUserIds).
export function usersLosingConnectorAccess(
  currentGrants: readonly GrantRow[],
  nextGrants: readonly GrantEntry[],
  userGroupMemberships: ReadonlyMap<string, readonly string[]>,
  groupMembers: ReadonlyMap<string, readonly string[]>,
  customRoles: readonly CustomRoleLike[]
): string[] {
  const nextUserIds = new Set(
    nextGrants.flatMap((g) => (g.userId ? [g.userId] : []))
  );
  const nextGroupIds = new Set(
    nextGrants.flatMap((g) => (g.userGroupId ? [g.userGroupId] : []))
  );
  const candidates = new Set<string>();
  for (const g of currentGrants) {
    if (g.userId && !nextUserIds.has(g.userId)) candidates.add(g.userId);
    if (g.userGroupId && !nextGroupIds.has(g.userGroupId))
      for (const m of groupMembers.get(g.userGroupId) ?? []) candidates.add(m);
  }
  if (candidates.size === 0) return [];
  const roleById = new Map(customRoles.map((r) => [r.id, r] as const));
  const nextSupplierGrants = nextGrants.map((g) =>
    stagedToGrantRow(g, roleById)
  );
  return [...candidates].filter(
    (userId) =>
      !principalHasSupplierPermissionFromGrants(
        userId,
        userGroupMemberships.get(userId) ?? [],
        nextSupplierGrants,
        "connected_app:use"
      )
  );
}

// Removing a space's access to a supplier means dropping the supplier grants
// held by the space's own principals. A principal who reaches the supplier
// another way (e.g. via a group not on the space) keeps that route, so the
// space may remain usable.
export function grantsWithoutSpacePrincipals<
  G extends {
    userGroupId?: string | null;
    userId?: string | null;
    serviceAccountId?: string | null;
  },
>(
  supplierGrants: readonly G[],
  spacePrincipals: readonly {
    userGroupId?: string | null;
    userId?: string | null;
    serviceAccountId?: string | null;
  }[]
): { next: G[]; removed: G[] } {
  const groupIds = new Set(
    spacePrincipals.map((g) => g.userGroupId).filter((id) => id != null)
  );
  const userIds = new Set(
    spacePrincipals.map((g) => g.userId).filter((id) => id != null)
  );
  const serviceAccountIds = new Set(
    spacePrincipals.map((g) => g.serviceAccountId).filter((id) => id != null)
  );
  const next: G[] = [];
  const removed: G[] = [];
  for (const grant of supplierGrants) {
    const hit =
      (grant.userGroupId != null && groupIds.has(grant.userGroupId)) ||
      (grant.userId != null && userIds.has(grant.userId)) ||
      (grant.serviceAccountId != null &&
        serviceAccountIds.has(grant.serviceAccountId));
    (hit ? removed : next).push(grant);
  }
  return { next, removed };
}

// One preview per distinct principal in a set of removed grants, for the
// "who loses access" warning.
export function removedPrincipalPreviews(
  removed: readonly {
    userGroupId?: string | null;
    userId?: string | null;
    serviceAccountId?: string | null;
  }[],
  lookups: {
    groupById: ReadonlyMap<string, { name: string }>;
    userById: ReadonlyMap<string, Row["user"]>;
    serviceAccountById: ReadonlyMap<string, { name: string }>;
  }
): PrincipalPreview[] {
  const seen = new Set<string>();
  const principals: PrincipalPreview[] = [];
  for (const grant of removed) {
    const key = grant.userGroupId
      ? `group-${grant.userGroupId}`
      : grant.serviceAccountId
        ? `serviceAccount-${grant.serviceAccountId}`
        : `user-${grant.userId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    if (grant.userGroupId) {
      principals.push({
        id: grant.userGroupId,
        name: lookups.groupById.get(grant.userGroupId)?.name ?? "Unknown group",
        kind: "group",
      });
    } else if (grant.serviceAccountId) {
      principals.push({
        id: grant.serviceAccountId,
        name:
          lookups.serviceAccountById.get(grant.serviceAccountId)?.name ??
          "Unknown service account",
        kind: "serviceAccount",
      });
    } else if (grant.userId) {
      const user = lookups.userById.get(grant.userId);
      principals.push({
        id: grant.userId,
        name: user ? userDisplayName(user) : "Unknown member",
        kind: "user",
        user,
      });
    }
  }
  return principals;
}

// Given the full next grant list, returns the spaces that are usable today but
// would stop being usable once the grants are committed.
export function spacesLosingSupplierAccess<
  S extends { id: string; roleGrantsAsScope: readonly GrantRow[] },
>(
  ruleId: CrossResourceAvailabilityRuleId,
  spaces: readonly S[],
  currentSupplierGrants: readonly GrantRow[],
  nextGrants: readonly GrantEntry[],
  userGroupMemberships: ReadonlyMap<string, readonly string[]>,
  customRoles: readonly CustomRoleLike[]
): S[] {
  const roleById = new Map(customRoles.map((r) => [r.id, r] as const));
  const nextSupplierGrants = nextGrants.map((g) =>
    stagedToGrantRow(g, roleById)
  );
  return spaces.filter(
    (space) =>
      isSupplierAvailableFromGrants(
        ruleId,
        space.roleGrantsAsScope,
        currentSupplierGrants,
        userGroupMemberships
      ) &&
      !isSupplierAvailableFromGrants(
        ruleId,
        space.roleGrantsAsScope,
        nextSupplierGrants,
        userGroupMemberships
      )
  );
}

// Given pending (staged) supplier grants, returns the spaces that are not usable
// today but would become usable once the grants are committed.
export function spacesGainingSupplierAccess<
  S extends { id: string; roleGrantsAsScope: readonly GrantRow[] },
>(
  ruleId: CrossResourceAvailabilityRuleId,
  spaces: readonly S[],
  currentSupplierGrants: readonly GrantRow[],
  stagedGrants: readonly GrantEntry[],
  userGroupMemberships: ReadonlyMap<string, readonly string[]>,
  customRoles: readonly CustomRoleLike[]
): S[] {
  if (stagedGrants.length === 0) return [];
  const roleById = new Map(customRoles.map((r) => [r.id, r] as const));
  const augmentedSupplierGrants = [
    ...currentSupplierGrants,
    ...stagedGrants.map((g) => stagedToGrantRow(g, roleById)),
  ];
  return spaces.filter(
    (space) =>
      !isSupplierAvailableFromGrants(
        ruleId,
        space.roleGrantsAsScope,
        currentSupplierGrants,
        userGroupMemberships
      ) &&
      isSupplierAvailableFromGrants(
        ruleId,
        space.roleGrantsAsScope,
        augmentedSupplierGrants,
        userGroupMemberships
      )
  );
}
